import { useMemo, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/States';
import { InfoTip } from '../../../components/ui/InfoTip';
import { Nil, TTotalRow, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import {
  BAR_RADIUS_VERTICAL,
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_LEGEND,
  CHART_SERIES,
  CHART_TOOLTIP,
} from '../../../components/ui/chart';
import { formatDayMonth, formatDayMonthYear } from '../../../utils/dateFormat';
import { formatNumber } from '../../../utils/numberFormat';
import {
  EFFORT_CHANNELS,
  EFFORT_CHANNEL_LABELS,
  type EffortChannel,
  type OutboundReport,
  type RepWeek,
  type WeekTotal,
} from '../../../api/outbound';
import type { ReplyBridge } from '../../../api/outreach';
import { failureHeading, periodFailureReasons, silentRepsLine } from './activityText';

const MAX_FAILURE_REASONS = 5;

/** "2026-08-25" → "25 Aug" (a UTC calendar day). */
export function weekDay(isoWeek: string): string {
  return formatDayMonth(`${isoWeek}T12:00:00Z`);
}

/** "42%" — the acceptance cohort rate; "—" when nobody was invited. */
function formatAcceptance(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

function AcceptanceCell({ accepted, invited, rate }: { accepted: number; invited: number; rate: number | null }) {
  if (rate === null) return <Nil />;
  return (
    <span className="tabular-nums whitespace-nowrap">
      <span className="block">{formatAcceptance(rate)}</span>
      {invited > 0 && <span className="block text-body-xs text-text-tertiary">{accepted}/{invited}</span>}
    </span>
  );
}

function repLabel(row: Pick<RepWeek, 'rep' | 'repName' | 'repEmail'>): string {
  if (row.rep === 'unattributed') return 'Campaign-level';
  return row.repName || row.repEmail || row.rep;
}

export const ACTIVITY_INFO = (
  <>
    Touches are every action lemlist actually performed (LinkedIn invites and messages, emails, the rare voice note or
    manual task) plus the calls the SDRs (Masa and Jovana) place in Apollo. Nothing here is self-reported.
    LinkedIn work done outside lemlist and Apollo emails are not counted yet. People reached and acceptance
    are lemlist only: Apollo calls carry no person to count. Acceptance is a cohort: of the people a rep
    invited that week, how many have accepted so far. Weeks run Monday–Sunday, Brussels time. The SDR calls go
    back to March, the lemlist data starts in September, so the SDRs filter shows the longer history.
  </>
);




/** Everything the activity panels derive from one report, for the last complete week. */
export function useActivityView(data: OutboundReport | undefined) {
  return useMemo(() => {
    if (!data) return null;
    const week = data.lastCompleteWeek;
    const rows = data.rows
      .filter((r) => r.week === week && (r.totalTouches > 0 || r.replies > 0 || r.failures > 0 || r.messagesBeforeAccept > 0))
      .sort((a, b) => b.totalTouches - a.totalTouches);
    const busy = new Set(data.rows.filter((r) => r.week === week && r.totalTouches > 0).map((r) => r.rep));
    // Walter is on the roster but in no group, so he is never reported as silent; a group view lists only its own people.
    const silentReps = data.reps.filter(
      (rep) => rep.group !== null && (!data.group || rep.group === data.group) && !rep.repKeys.some((key) => busy.has(key)),
    );
    // The selected period: every complete week the report covers (the week in
    // progress is left out). Distinct-people counts don't add across weeks, so
    // only the summable totals follow the period.
    const fullWeeks = data.weeks.filter((w) => w !== data.currentWeek);
    // Over the same full weeks as the Failed actions tile, so the list adds up to it.
    const failureReasons = periodFailureReasons(data.failureReasons, fullWeeks).slice(0, MAX_FAILURE_REASONS);
    /** campaign id → messages fired before the invite was accepted, in the headline week. */
    const sequenceWarnings = new Map<string, number>();
    for (const w of data.sequenceWarnings) {
      if (w.week === week && w.count > 0 && w.campaignId) sequenceWarnings.set(w.campaignId, w.count);
    }
    const totals: WeekTotal = data.weekTotals.find((t) => t.week === week) ?? {
      week,
      touches: 0,
      leadsTouched: 0,
      replies: 0,
      invitedPeople: 0,
      invitesAccepted: 0,
      acceptanceRate: null,
      failures: 0,
      messagesBeforeAccept: 0,
    };
    const inPeriod = data.weekTotals.filter((t) => fullWeeks.includes(t.week));
    const sum = (pick: (t: WeekTotal) => number) => inPeriod.reduce((n, t) => n + pick(t), 0);
    const invitedPeople = sum((t) => t.invitedPeople);
    const invitesAccepted = sum((t) => t.invitesAccepted);
    // Apollo calls are in Touches but not in the lemlist campaign rows, so the campaign footer needs them apart.
    const apolloCalls = data.rows
      .filter((r) => r.rep.startsWith('apollo:') && fullWeeks.includes(r.week))
      .reduce((n, r) => n + r.totalTouches, 0);
    const period = {
      fullWeeks: fullWeeks.length,
      touches: sum((t) => t.touches),
      apolloCalls,
      replies: sum((t) => t.replies),
      failures: sum((t) => t.failures),
      invitedPeople,
      invitesAccepted,
      acceptanceRate: invitedPeople > 0 ? invitesAccepted / invitedPeople : null,
    };
    return { data, week, rows, silentReps, failureReasons, sequenceWarnings, totals, period };
  }, [data]);
}

export type ActivityView = NonNullable<ReturnType<typeof useActivityView>>;

/**
 * What the report actually covers, in words: "Last 12 full weeks", or, when
 * lemlist data starts later than the selected range, "5 full weeks, since
 * lemlist data starts (1 Sep)". The API never returns weeks before the first
 * synced activity, so a 12-week selection can hold fewer weeks.
 */
export function coveredWeeksLabel(view: ActivityView, requestedWeeks: number | 'all'): string {
  const n = view.period.fullWeeks;
  const start = view.data.earliestActivityAt ? ` (${formatDayMonth(view.data.earliestActivityAt)})` : '';
  if (requestedWeeks === 'all' || n < requestedWeeks) {
    return `${n} full week${n === 1 ? '' : 's'}, since ${view.data.group === 'sdr' ? 'the SDR call data' : 'lemlist data'} starts${start}`;
  }
  return `Last ${n} full weeks`;
}

/**
 * "55 reply messages = 33 people in the rows below + 12 repeat replies + 10 in
 * finished campaigns": how the Replies tile reconciles with the Replied column
 * of the signal and campaign rows. Null when the two windows disagree, so a
 * bridge is never shown that does not add up.
 */
export function replyBridgeText(bridge: ReplyBridge | null | undefined, tileReplies: number): string | null {
  if (!bridge || bridge.messages !== tileReplies) return null;
  const n = (v: number) => formatNumber(v);
  const parts = [`${n(bridge.listedPeople)} ${bridge.listedPeople === 1 ? 'person' : 'people'} in the signal and campaign rows`];
  if (bridge.repeatMessages > 0) parts.push(`${n(bridge.repeatMessages)} repeat ${bridge.repeatMessages === 1 ? 'reply' : 'replies'} from them`);
  if (bridge.otherCampaignMessages > 0) parts.push(`${n(bridge.otherCampaignMessages)} in finished or archived campaigns`);
  if (bridge.botMessages > 0) parts.push(`${n(bridge.botMessages)} flagged as bot activity`);
  return `${n(bridge.messages)} reply messages = ${parts.join(' + ')}`;
}

/**
 * The lemlist KPI tiles that follow the selected period: touches, acceptance,
 * replies and failures add up across weeks. The period is said once, above the
 * tiles, so each sub-line names only the source and the detail.
 * "Failed actions" only shows when something failed.
 */
export function periodTiles(view: ActivityView, bridge?: ReplyBridge | null): ReactNode[] {
  const { period } = view;
  // The SDRs work in Apollo, not lemlist: invites, replies and failures would
  // always read 0 for them, which says "nothing happened" when it means "not tracked".
  if (view.data.group === 'sdr') {
    return [
      <StatTile
        key="touches"
        label="Touches"
        value={formatNumber(period.touches)}
        sub="Apollo calls · LinkedIn work and emails are not tracked yet"
      />,
    ];
  }
  const bridgeText = replyBridgeText(bridge, period.replies);
  const tiles: ReactNode[] = [
    <StatTile key="touches" label="Touches" value={formatNumber(period.touches)} sub="lemlist actions + SDR Apollo calls" />,
    <StatTile
      key="accepted"
      label="Invites accepted"
      value={formatAcceptance(period.acceptanceRate)}
      sub={
        period.invitedPeople > 0
          ? `${formatNumber(period.invitesAccepted)} of ${formatNumber(period.invitedPeople)} invited · lemlist`
          : 'Nobody invited'
      }
      tooltip="Of the people invited each week, how many have accepted so far, summed over the weeks."
    />,
    <StatTile
      key="replies"
      label="Replies"
      value={formatNumber(period.replies)}
      sub={bridge && bridgeText ? `Messages · ${formatNumber(bridge.listedPeople)} people in the rows below` : 'Reply messages · lemlist'}
      tooltip={
        bridgeText
          ? `Every reply lemlist recorded, so someone who replied twice counts twice. ${bridgeText}.`
          : 'Every reply lemlist recorded, so someone who replied twice counts twice. The campaign table counts people who replied.'
      }
    />,
  ];
  if (period.failures > 0) {
    tiles.push(<StatTile key="failed" label="Failed actions" value={formatNumber(period.failures)} sub="lemlist" />);
  }
  return tiles;
}

/** "7 Sep – 4 Oct": the first to the last full week of the selected period. */
export function periodRange(view: ActivityView): string {
  const fullWeeks = view.data.weeks.filter((w) => w !== view.data.currentWeek);
  const first = fullWeeks[0];
  if (!first) return lastWeekRange(view);
  const end = new Date(`${view.week}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${weekDay(first)} – ${formatDayMonth(end.toISOString())}`;
}

/** "28 Sep – 4 Oct": the last full Monday–Sunday week. */
export function lastWeekRange(view: ActivityView): string {
  const end = new Date(`${view.week}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${weekDay(view.week)} – ${formatDayMonth(end.toISOString())}`;
}

/** People reached is a distinct count that can't be summed across weeks, so it stays on the last full week. */
export function peopleReachedTile(view: ActivityView): ReactNode {
  return (
    <StatTile
      key="people"
      label="People reached"
      value={formatNumber(view.totals.leadsTouched)}
      sub={`Last full week (${lastWeekRange(view)}) · distinct people · lemlist only`}
      tooltip="A distinct count: the same person touched in two weeks counts once, so it can't be summed across the period and stays on the last full week."
    />
  );
}

/**
 * Full Mon–Sun weeks of lemlist data that exist so far: from the week the first
 * activity was synced up to the last complete week. Null when nothing synced.
 */
export function availableFullWeeks(earliestActivityAt: string | null, now: Date = new Date()): number | null {
  if (!earliestActivityAt) return null;
  const monday = (d: Date) => {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
    return x.getTime();
  };
  return Math.max(0, Math.round((monday(now) - monday(new Date(earliestActivityAt))) / (7 * 86_400_000)));
}

/**
 * Colours for the reps in the touches chart, from the shared non-indigo series palette (ink, umber,
 * slate, sand, stone, graphite): indigo stays for links and selection, and no two reps look alike.
 * The team keeps its roster position (Patrick, Nadia, Masa, Jovana, Walter), so a person reads the
 * same on every chart and in every group view; other seats (Andrew) follow after them.
 */
const REP_COLORS = [CHART_SERIES[0], CHART_SERIES[1], CHART_SERIES[3], CHART_SERIES[4], CHART_SERIES[2], CHART_SERIES[5]] as const;

function repColor(view: ActivityView, rep: string, index: number): string {
  const teamIndex = view.data.reps.findIndex((r) => r.repKeys.includes(rep));
  const i = teamIndex >= 0 ? teamIndex : index + view.data.reps.length;
  return REP_COLORS[i % REP_COLORS.length]!;
}

/**
 * The channel columns for a week: every channel someone used that week (the
 * usual three, voice notes, calls, manual tasks), so the columns add up to
 * Touches. A channel nobody used is left out rather than drawn as a column of
 * dots (Emails, Oct 2026: every rep was LinkedIn-only).
 */
export function weekChannels(rows: readonly Pick<RepWeek, 'effort'>[]): EffortChannel[] {
  return EFFORT_CHANNELS.filter((c) => rows.some((r) => (r.effort[c] ?? 0) > 0));
}

/** Per-rep table for the last complete week, with silent reps listed at zero and a team total. */
export function RepWeekTable({ view }: { view: ActivityView }) {
  const { rows, silentReps, failureReasons, totals, period } = view;
  // A Failures column of dots says nothing: it shows only when an action failed.
  const showFailures = rows.some((r) => r.failures > 0);
  const channels = weekChannels(rows);
  const sum = (pick: (r: RepWeek) => number) => rows.reduce((n, r) => n + pick(r), 0);
  const silentLine = silentRepsLine(silentReps);
  const columnCount = 5 + channels.length + (showFailures ? 1 : 0);
  const listedFailures = failureReasons.reduce((n, r) => n + r.count, 0);
  return (
    <Card padding="md">
      <CardHeader
        title={`Per rep · last full week (${lastWeekRange(view)})`}
        description="Fixed to the last complete Monday–Sunday week, whatever the period above. The channel columns add up to Touches; profile visits are not touches."
      />
      <Table minWidth={800}>
        <THead>
          <tr>
            <Th>Rep</Th>
            <Th align="right">Touches</Th>
            {channels.map((channel) => (
              <Th key={channel} align="right">{EFFORT_CHANNEL_LABELS[channel]}</Th>
            ))}
            <Th align="right">People</Th>
            <Th align="right" title="Of the people invited that week, how many have accepted so far">Accepted</Th>
            <Th align="right">Replies</Th>
            {showFailures && <Th align="right">Failures</Th>}
          </tr>
        </THead>
        <tbody>
          {rows.map((row) => (
            <Tr key={row.rep}>
              <Td>{repLabel(row)}</Td>
              <Td align="right" className="font-medium">{row.totalTouches}</Td>
              {channels.map((channel) => (
                <Td key={channel} align="right">{row.effort[channel] || <Nil />}</Td>
              ))}
              <Td align="right">{row.leadsTouched}</Td>
              <Td align="right">
                <AcceptanceCell accepted={row.invitesAccepted} invited={row.invitedPeople} rate={row.acceptanceRate} />
              </Td>
              <Td align="right">{row.replies}</Td>
              {showFailures && <Td align="right">{row.failures || <Nil />}</Td>}
            </Tr>
          ))}
          {/* Reps with no touches all week: one amber line, not a row of zeros each. */}
          {silentLine && (
            <Tr className="bg-warning-light">
              <Td colSpan={columnCount} className="font-medium text-warning-dark">
                {silentLine}
              </Td>
            </Tr>
          )}
          {/* The team line only when two or more rows add up to it: with one active rep it would repeat that row. */}
          {rows.length > 1 && (
            <TTotalRow>
              <Td>Team</Td>
              <Td align="right">{sum((r) => r.totalTouches)}</Td>
              {channels.map((channel) => (
                <Td key={channel} align="right">{sum((r) => r.effort[channel] ?? 0) || <Nil />}</Td>
              ))}
              {/* One person touched by two reps counts once here, so People is less than the column sum. */}
              <Td align="right" title="Distinct people across the team: someone two reps touched counts once">{totals.leadsTouched}</Td>
              <Td align="right">
                <AcceptanceCell accepted={totals.invitesAccepted} invited={totals.invitedPeople} rate={totals.acceptanceRate} />
              </Td>
              <Td align="right">{totals.replies}</Td>
              {showFailures && <Td align="right">{totals.failures || <Nil />}</Td>}
            </TTotalRow>
          )}
        </tbody>
      </Table>

      {failureReasons.length > 0 && (
        <div className="mt-4 border-t border-border-light pt-3">
          <p className="text-body-xs text-text-tertiary mb-1">
            {failureHeading(periodRange(view), period.failures, listedFailures, failureReasons.length, totals.failures)}
          </p>
          <ul className="divide-y divide-border-light">
            {failureReasons.map((r) => (
              <li key={r.reason} className="flex items-start justify-between gap-4 py-1.5 text-body-sm">
                <span className="text-text-secondary">{r.reason}</span>
                <span className="tabular-nums font-medium text-text-primary">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** Touches per rep per week, in the team colours; the current week is marked as still in progress. */
export function TouchesChart({ view, weeksLabel }: { view: ActivityView; weeksLabel: string }) {
  const { data } = view;

  const trendReps = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of data.rows) {
      if (row.totalTouches > 0 && !seen.has(row.rep)) seen.set(row.rep, repLabel(row));
    }
    return [...seen.entries()].map(([rep, label], i) => ({ rep, label, color: repColor(view, rep, i) }));
  }, [data, view]);

  const trendData = useMemo(
    () =>
      data.weeks.map((week) => {
        // The tick stays short; "(so far)" for the current week goes in the tooltip.
        const point: Record<string, string | number> = { week: weekDay(week), __current: week === data.currentWeek ? 1 : 0 };
        for (const { rep } of trendReps) {
          point[rep] = data.rows.find((r) => r.week === week && r.rep === rep)?.totalTouches ?? 0;
        }
        return point;
      }),
    [data, trendReps],
  );

  return (
    <Card padding="md">
      <CardHeader
        title="Touches per week"
        description={`${weeksLabel}, plus the current week in progress`}
        action={
          data.earliestActivityAt ? (
            <InfoTip label="About this chart">
              There is no data before {formatDayMonthYear(data.earliestActivityAt)}, when the first activity was
              synced.{data.group === 'sdr' ? '' : ' SDR calls older than that are left out here; the SDRs filter shows them.'} Earlier weeks are left out rather than shown as empty, because no data is not the same as no work. A
              single tall bar next to empty weeks is a burst, not a habit.
            </InfoTip>
          ) : undefined
        }
      />
      {trendReps.length === 0 ? (
        <EmptyState title="No outbound activity in this period" compact />
      ) : (
        <div style={{ width: '100%', height: CHART_HEIGHT.default }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="week" {...CHART_AXIS} interval="preserveStartEnd" minTickGap={28} />
              <YAxis {...CHART_AXIS} allowDecimals={false} tickCount={4} />
              <Tooltip
                {...CHART_TOOLTIP}
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.__current ? `Week of ${label} (so far)` : `Week of ${label}`
                }
              />
              {/* Ink legend text: Recharts paints it in the series colour, so the light-grey rep read as switched off. */}
              <Legend {...CHART_LEGEND} formatter={(value) => <span className="text-text-secondary">{value}</span>} />
              {trendReps.map((rep) => (
                <Bar
                  key={rep.rep}
                  dataKey={rep.rep}
                  name={rep.label}
                  fill={rep.color}
                  radius={[...BAR_RADIUS_VERTICAL]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function noActivityYet(): ReactNode {
  return (
    <EmptyState
      title="No lemlist activity has synced yet"
      description="Add the lemlist API key in Settings → Integrations, then wait for the daily sync (or trigger it from the Integrations page)."
    />
  );
}
